import { useCallback } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { ArchiveFill } from '@lyearn/icons';
import { useSnackbar } from '@lyearn/ui';
import useDefaultErrorSnackbar from '@/hooks/useDefaultErrorSnackbar';
import useResponse from '@/hooks/useResponse';
import { RecommendedActionStatusEnum } from '@/types/schema';
import { UpdateStatusOfRecommendedActionsInput } from '@/types/schema';

import { useUpdateStatusOfRecommendedActionsMutation as useGeneratedMutation } from '../graphql/mutations/generated/UpdateStatusOfRecommendedActions';

function useUpdateRecommendationStatusMutation() {
  const [result, executeMutation] = useGeneratedMutation();
  const { handleResponse } = useResponse();
  const { showDefaultErrorSnackbar } = useDefaultErrorSnackbar();
  const { t } = useTranslation(['Home']);
  const { showSnackbar } = useSnackbar();

  const updateStatus = useCallback(
    async (
      input: UpdateStatusOfRecommendedActionsInput,
      onSuccess?: () => void,
      onError?: () => void,
    ) => {
      const response = await executeMutation({ input });

      handleResponse({
        condition:
          !response.error &&
          (response.data?.updateStatusOfRecommendedActions?.recommendedActions ? true : false),
        onSuccess: () => {
          onSuccess?.();
          if (input.status === RecommendedActionStatusEnum.Seen) return;
          showSnackbar({
            message: t('Home:AiRecommendationWidget.Success', {
              action: input.status.toLowerCase(),
            }),
            iconStatus: 'success',
            StartIcon: ArchiveFill,
          });
        },
        onError: () => {
          showDefaultErrorSnackbar();
          onError?.();
        },
      });

      const status = response?.data?.updateStatusOfRecommendedActions?.recommendedActions
        ? response?.data?.updateStatusOfRecommendedActions?.recommendedActions[0]?.status
        : undefined;

      return {
        currentStatus: status,
        error: response.error,
      };
    },
    [executeMutation, handleResponse, showSnackbar, t, showDefaultErrorSnackbar],
  );

  return {
    updateStatus,
    isLoading: result.fetching,
    error: result.error,
  };
}

export default useUpdateRecommendationStatusMutation;
